import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Divider,
  IconButton,
  Paper,
  InputBase,
  CircularProgress,
  Avatar,
  AvatarGroup,
  Tooltip,
  Alert,
  Badge,
  Popper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Collapse,
  ClickAwayListener,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Menu as MenuIcon,
  Send as SendIcon,
  Tag as TagIcon,
  Person as PersonIcon,
  Info as InfoIcon,
  WifiOff as WifiOffIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  DeleteForever as DeleteForeverIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  EmojiEmotions as EmojiIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import EmojiPicker from 'emoji-picker-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/auth/AuthModule';
import { message as messageApi, user as userApi } from '../../../utils/api';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';
import MessageItem from './MessageItem';
import socketManager from '../../../utils/socketManager';
import EditChannelDialog from './EditChannelDialog';
import { useTheme } from '@mui/material/styles';

const ChatWindow = ({ 
  channel, 
  onToggleSidebar, 
  sidebarVisible, 
  isMobile, 
  onStartDirectMessage,
  onLockChannel,
  onUnlockChannel,
  onDeleteChannel,
  onEditChannel,
  onRemoveUser
}) => {
  const { t } = useTranslation(['common']);
  const { user } = useAuth();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);
  const [mentionUsers, setMentionUsers] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionAnchorEl, setMentionAnchorEl] = useState(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const previousChannelIdRef = useRef(null);
  const [adminMenuAnchorEl, setAdminMenuAnchorEl] = useState(null);
  const adminMenuOpen = Boolean(adminMenuAnchorEl);
  const [editChannelDialogOpen, setEditChannelDialogOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef(null);
  const [memberDetailsAnchorEl, setMemberDetailsAnchorEl] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);

  // Initialize Socket.IO and set up event listeners
  useEffect(() => {
    const socket = socketManager.initSocket();
    
    // Check initial connection status
    setSocketConnected(socketManager.isConnected());
    
    // Periodically check connection status (handles cases where events might be missed)
    const connectionChecker = setInterval(() => {
      const isCurrentlyConnected = socketManager.isConnected();
      if (socketConnected !== isCurrentlyConnected) {
        console.log('Connection status changed:', isCurrentlyConnected);
        setSocketConnected(isCurrentlyConnected);
      }
    }, 5000);
    
    // Socket connection status
    const handleConnect = () => {
      console.log('Socket connected event received');
      setSocketConnected(true);
      
      // Rejoin channel on reconnection if needed
      if (channel && channel._id) {
        socketManager.joinChannel(channel._id);
      }
    };
    
    const handleDisconnect = () => {
      console.log('Socket disconnected event received');
      setSocketConnected(false);
    };
    
    const handleError = (error) => {
      console.log('Socket error event received', error);
      setSocketConnected(false);
    };
    
    // New message handler
    const handleNewMessage = (data) => {
      console.log('New message received:', data);
      setMessages(prevMessages => [...prevMessages, data.message]);
    };
    
    // Update message handler
    const handleUpdateMessage = (data) => {
      console.log('Message updated:', data);
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id === data.message._id ? data.message : msg
        )
      );
    };
    
    // Delete message handler
    const handleDeleteMessage = (data) => {
      console.log('Message deleted:', data);
      setMessages(prevMessages => 
        prevMessages.filter(msg => msg._id !== data.messageId)
      );
    };
    
    // Register event listeners
    socketManager.addEventListener('connect', handleConnect);
    socketManager.addEventListener('disconnect', handleDisconnect);
    socketManager.addEventListener('error', handleError);
    socketManager.addEventListener('new-message', handleNewMessage);
    socketManager.addEventListener('update-message', handleUpdateMessage);
    socketManager.addEventListener('delete-message', handleDeleteMessage);
    
    // Cleanup event listeners and interval on unmount
    return () => {
      clearInterval(connectionChecker);
      socketManager.removeEventListener('connect', handleConnect);
      socketManager.removeEventListener('disconnect', handleDisconnect);
      socketManager.removeEventListener('error', handleError);
      socketManager.removeEventListener('new-message', handleNewMessage);
      socketManager.removeEventListener('update-message', handleUpdateMessage);
      socketManager.removeEventListener('delete-message', handleDeleteMessage);
    };
  }, []);

  // Handle channel changes
  useEffect(() => {
    if (channel) {
      // Leave previous channel
      if (previousChannelIdRef.current) {
        socketManager.leaveChannel(previousChannelIdRef.current);
      }
      
      // Join new channel
      socketManager.joinChannel(channel._id);
      previousChannelIdRef.current = channel._id;
      
      // Fetch messages for the new channel
      fetchMessages();
      
      // Mark messages as read when joining a channel
      markMessagesAsRead();
    } else {
      setMessages([]);
    }
    
    // Clean up when component unmounts
    return () => {
      if (previousChannelIdRef.current) {
        socketManager.leaveChannel(previousChannelIdRef.current);
      }
    };
  }, [channel]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
    
    // Mark messages as read when new messages arrive
    if (channel && messages.length > 0) {
      markMessagesAsRead();
    }
  }, [messages]);

  // Fetch channel members for mentions when channel changes
  useEffect(() => {
    if (channel && channel.members && channel.members.length > 0) {
      // Channel members are already available for mentions
      setMentionUsers(channel.members.filter(member => member._id !== user._id));
    }
  }, [channel, user]);

  const fetchMessages = async () => {
    if (!channel) return;
    
    try {
      setLoading(true);
      const response = await messageApi.getChannelMessages(channel._id);
      setMessages(response.messages.reverse() || []); // Reverse to show oldest first
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error(t('error.fetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    if (!channel || !user) return;
    
    try {
      console.log('Marking messages as read for channel:', channel._id);
      const result = await messageApi.markAsRead(channel._id);
      console.log('Messages marked as read:', result);
      
      // When manually marking messages as read, update the state of the parent
      // component by explicitly emitting a socket event to ourselves
      socketManager.emitMessagesRead(channel._id, user._id);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !channel) return;

    try {
      setSendingMessage(true);
      
      // Double-check current connection status
      const currentlyConnected = socketManager.isConnected();
      if (!currentlyConnected && socketConnected) {
        // Socket appears connected in state but actually isn't
        setSocketConnected(false);
      }
      
      // Only show warning if not connected (and update state to match reality)
      if (!currentlyConnected) {
        toast.warning(t('comms.noRealtimeConnection'));
      }
      
      const response = await messageApi.create(channel._id, newMessage.trim());
      
      // Only add the message locally if socket is truly not connected
      if (!currentlyConnected) {
        setMessages(prevMessages => [...prevMessages, response.message]);
      }
      
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('error.sendingMessage'));
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await messageApi.delete(messageId);
      
      // Only update locally if socket is not connected
      if (!socketConnected) {
        setMessages(prevMessages => prevMessages.filter(msg => msg._id !== messageId));
        toast.success(t('comms.messageDeleted'));
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error(t('error.deletingMessage'));
    }
  };

  const handleEditMessage = async (messageId, newContent) => {
    try {
      const response = await messageApi.update(messageId, newContent);
      
      // Only update locally if socket is not connected
      if (!socketConnected) {
        setMessages(prevMessages => 
          prevMessages.map(msg => 
            msg._id === messageId ? response.message : msg
          )
        );
        toast.success(t('comms.messageUpdated'));
      }
    } catch (error) {
      console.error('Error editing message:', error);
      toast.error(t('error.updatingMessage'));
    }
  };

  const handleStartDirectMessage = (sender) => {
    if (onStartDirectMessage) {
      onStartDirectMessage(sender);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // For DMs, show the other user's name
  const getChannelDisplayName = () => {
    if (!channel) return '';
    
    if (channel.type === 'private') {
      const otherUser = channel.members?.find(member => 
        member._id !== user._id
      );
      return otherUser ? (otherUser.name || otherUser.username) : channel.name;
    }
    
    return channel.name;
  };

  // Handle message input with @ mention support
  const handleMessageChange = async (e) => {
    const newValue = e.target.value;
    setNewMessage(newValue);
    
    // Get cursor position
    const cursorPos = e.target.selectionStart;
    setCursorPosition(cursorPos);
    
    // Check for @ mentions
    if (channel && channel.members) {
      // Find if we're currently typing an @mention
      const textBeforeCursor = newValue.substring(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf('@');
      
      if (atIndex !== -1 && 
          (atIndex === 0 || /\s/.test(textBeforeCursor.charAt(atIndex - 1)))) {
        // We are typing a mention
        const query = textBeforeCursor.substring(atIndex + 1);
        setMentionQuery(query);
        
        // Filter users based on query
        const filteredUsers = channel.members.filter(member => 
          // Don't show current user in mentions
          member._id !== user._id && 
          // Filter by name or username containing the query (case insensitive)
          (member.name?.toLowerCase().includes(query.toLowerCase()) || 
           member.username?.toLowerCase().includes(query.toLowerCase()))
        );
        
        setMentionUsers(filteredUsers);
        setMentionAnchorEl(inputRef.current);
      } else {
        // Not typing a mention, close the popup
        setMentionAnchorEl(null);
      }
    }
  };

  // Handle selecting a user from mentions
  const handleSelectMention = (selectedUser) => {
    // Find the @ position
    const textBeforeCursor = newMessage.substring(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1) {
      // Replace the @query with @username
      const textBefore = newMessage.substring(0, atIndex);
      const textAfter = newMessage.substring(cursorPosition);
      const username = selectedUser.username || selectedUser.name;
      
      const newText = `${textBefore}@${username} ${textAfter}`;
      setNewMessage(newText);
      
      // Position cursor after the inserted mention
      setTimeout(() => {
        if (inputRef.current) {
          const newCursorPos = atIndex + username.length + 2; // +2 for @ and space
          inputRef.current.focus();
          inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
          setCursorPosition(newCursorPos);
        }
      }, 0);
    }
    
    // Close the mention popup
    setMentionAnchorEl(null);
  };

  const handleKeyDown = (e) => {
    // Update cursor position on key events
    if (inputRef.current) {
      setCursorPosition(inputRef.current.selectionStart);
    }
    
    // Handle up/down arrow navigation in mention list
    if (mentionAnchorEl && mentionUsers.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault(); // Prevent cursor movement in input
      } else if (e.key === 'Enter' && mentionUsers.length > 0) {
        // Select the first user on Enter
        e.preventDefault();
        handleSelectMention(mentionUsers[0]);
      } else if (e.key === 'Escape') {
        // Close mention popup on Escape
        setMentionAnchorEl(null);
      }
    }
    
    // Close emoji picker when pressing Escape
    if (e.key === 'Escape' && showEmojiPicker) {
      e.preventDefault();
      setShowEmojiPicker(false);
    }
  };

  // Group consecutive messages from the same sender
  const groupMessagesBySender = (messages) => {
    if (!messages || messages.length === 0) return [];
    
    const groups = [];
    let currentGroup = [messages[0]];
    
    for (let i = 1; i < messages.length; i++) {
      const prevMessage = messages[i - 1];
      const currentMessage = messages[i];
      
      // If the sender is the same and messages are within 10 minutes of each other, group them
      if (
        prevMessage.sender._id === currentMessage.sender._id &&
        new Date(currentMessage.createdAt) - new Date(prevMessage.createdAt) < 10 * 60 * 1000
      ) {
        currentGroup.push(currentMessage);
      } else {
        groups.push(currentGroup);
        currentGroup = [currentMessage];
      }
    }
    
    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }
    
    return groups;
  };

  // Admin menu handlers
  const handleAdminMenuOpen = (event) => {
    setAdminMenuAnchorEl(event.currentTarget);
  };

  const handleAdminMenuClose = () => {
    setAdminMenuAnchorEl(null);
  };

  const handleLockChannel = () => {
    if (onLockChannel) {
      onLockChannel(channel._id);
    }
    handleAdminMenuClose();
  };

  const handleUnlockChannel = () => {
    if (onUnlockChannel) {
      onUnlockChannel(channel._id);
    }
    handleAdminMenuClose();
  };

  const handleDeleteClick = () => {
    // For private channels (DMs), show a warning confirmation first
    if (channel.type === 'private') {
      setShowDeleteConfirmDialog(true);
    } else {
      handleDeleteChannel();
    }
    handleAdminMenuClose();
  };

  const handleDeleteChannel = () => {
    if (onDeleteChannel) {
      onDeleteChannel(channel._id);
    }
    setShowDeleteConfirmDialog(false);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmDialog(false);
  };

  const handleEditChannelClick = () => {
    setEditChannelDialogOpen(true);
    handleAdminMenuClose();
  };

  const handleEditChannel = async (channelId, channelData) => {
    if (onEditChannel) {
      await onEditChannel(channelId, channelData);
    }
  };

  // Handle emoji selection
  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    const textBeforeCursor = newMessage.substring(0, cursorPosition);
    const textAfterCursor = newMessage.substring(cursorPosition);
    
    // Insert emoji at cursor position
    const updatedMessage = textBeforeCursor + emoji + textAfterCursor;
    setNewMessage(updatedMessage);
    
    // Update cursor position
    const newPosition = cursorPosition + emoji.length;
    setCursorPosition(newPosition);
    
    // Focus input and set cursor position after emoji
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 10);
  };

  // Toggle emoji picker
  const handleToggleEmojiPicker = () => {
    setShowEmojiPicker(!showEmojiPicker);
    // Close mention popup if open
    if (mentionAnchorEl) {
      setMentionAnchorEl(null);
    }
  };

  // Close emoji picker when clicking outside
  const handleCloseEmojiPicker = () => {
    setShowEmojiPicker(false);
  };

  // Handle member avatar hover
  const handleMemberHover = (event, member) => {
    setMemberDetailsAnchorEl(event.currentTarget);
    setSelectedMember(member);
  };

  // Handle member details close
  const handleMemberDetailsClose = () => {
    setMemberDetailsAnchorEl(null);
    setSelectedMember(null);
  };

  // Handle showing all members dialog
  const handleShowAllMembers = () => {
    setShowMembersDialog(true);
    handleMemberDetailsClose();
  };

  // Handle member avatar click - open members dialog directly
  const handleMemberClick = () => {
    setShowMembersDialog(true);
  };

  if (!channel) {
    return (
      <Box 
        sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          flexDirection: 'column',
          p: 3,
          gap: 2
        }}
      >
        <InfoIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography variant="h6" color="text.secondary">
          {t('comms.selectChannel')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      {/* Channel Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          zIndex: 5,
        }}
      >
        {/* Left Section: Menu + Channel Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: '1 1 auto' }}>
          {isMobile && (
            <IconButton 
              edge="start" 
              onClick={onToggleSidebar}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {/* Channel Type Icon */}
          {channel.type === 'team' ? (
            <TagIcon sx={{ mr: 1, color: 'text.secondary' }} />
          ) : (
            <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
          )}
          
          <Typography variant="subtitle1" fontWeight="bold">
            {getChannelDisplayName()}
          </Typography>

          {channel.isLocked && (
            <Tooltip title={t('comms.channelLocked')}>
              <LockIcon sx={{ ml: 1, color: 'warning.main', fontSize: 18 }} />
            </Tooltip>
          )}
          
          {channel.description && (
            <Tooltip title={channel.description}>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <InfoIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Right Section: Members + Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Channel Members Avatar Group with count button */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {channel.members && channel.members.length > 0 && (
              <Tooltip title={t('comms.viewMembers')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AvatarGroup
                    max={5}
                    onClick={handleMemberClick}
                    sx={{
                      '& .MuiAvatar-root': {
                        width: 28,
                        height: 28,
                        fontSize: '0.875rem',
                        border: '2px solid',
                        borderColor: 'background.paper',
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: '0 0 0 2px primary.main'
                        }
                      },
                    }}
                  >
                    {/* Filter out duplicate members by ID */}
                    {channel.members
                      .filter((member, index, self) => 
                        index === self.findIndex(m => 
                          m._id === member._id
                        )
                      )
                      .map((member) => (
                        <Avatar
                          key={member._id}
                          alt={member.name || member.username}
                          src={member.imageUrl}
                          sx={{
                            bgcolor: !member.imageUrl ? 'primary.main' : 'transparent',
                          }}
                        >
                          {(member.name || member.username || '?')[0].toUpperCase()}
                        </Avatar>
                      ))
                    }
                  </AvatarGroup>
                  <IconButton 
                    size="small" 
                    onClick={handleMemberClick}
                    sx={{ ml: 0.5 }}
                    color="primary"
                  >
                    <GroupIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Tooltip>
            )}
          </Box>
          
          {/* Admin Controls */}
          {user && (
            // Show admin menu for:
            // 1. Team channels - if user is admin or channel creator
            // 2. Private channels (DMs) - for all participants 
            (channel.type === 'team' && (user.isAdmin || (channel.createdBy && channel.createdBy._id === user._id))) || 
            (channel.type === 'private')
          ) && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title={t('comms.channelOptions')}>
                <IconButton onClick={handleAdminMenuOpen} size="small">
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={adminMenuAnchorEl}
                open={adminMenuOpen}
                onClose={handleAdminMenuClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {/* Lock/Unlock options only available for team channels */}
                {channel.type === 'team' && (
                  <>
                    {channel.isLocked ? (
                      <MenuItem onClick={handleUnlockChannel}>
                        <ListItemIcon>
                          <LockOpenIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('comms.unlockChannel')} />
                      </MenuItem>
                    ) : (
                      <MenuItem onClick={handleLockChannel}>
                        <ListItemIcon>
                          <LockIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={t('comms.lockChannel')} />
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleEditChannelClick}>
                      <ListItemIcon>
                        <EditIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={t('comms.editChannel')} />
                    </MenuItem>
                    <Divider />
                  </>
                )}
                
                {/* Delete option available for all channels */}
                <MenuItem onClick={handleDeleteClick}>
                  <ListItemIcon>
                    <DeleteForeverIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={channel.type === 'private' ? t('comms.deleteConversation') : t('comms.deleteChannel')} 
                    primaryTypographyProps={{ color: 'error' }}
                  />
                </MenuItem>
              </Menu>
            </Box>
          )}
          
          {/* Socket connection status */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={socketConnected 
              ? t('comms.realtimeConnected') 
              : t('comms.noRealtimeConnection')
            }>
              <Badge 
                color={socketConnected ? "success" : "error"} 
                variant="dot"
                sx={{ mr: 0 }}
              >
                {socketConnected ? (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    color: 'success.main',
                    fontSize: '0.75rem' 
                  }}>
                    <span style={{ marginRight: '4px' }}>{t('comms.live')}</span>
                  </Box>
                ) : (
                  <WifiOffIcon fontSize="small" color="action" />
                )}
              </Badge>
            </Tooltip>
          </Box>
        </Box>

        {/* Member Details Popper */}
        <Popper
          open={Boolean(memberDetailsAnchorEl)}
          anchorEl={memberDetailsAnchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1400 }}
          onMouseLeave={handleMemberDetailsClose}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 2, 
              mt: 1, 
              minWidth: 220,
              maxWidth: 300,
              borderRadius: 2,
            }}
          >
            {selectedMember && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Avatar
                  src={selectedMember.imageUrl}
                  alt={selectedMember.name || selectedMember.username}
                  sx={{ width: 64, height: 64, mb: 1 }}
                >
                  {(selectedMember.name || selectedMember.username || '?')[0].toUpperCase()}
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold" textAlign="center">
                  {selectedMember.name || selectedMember.username}
                </Typography>
                {selectedMember.name && selectedMember.username && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    @{selectedMember.username}
                  </Typography>
                )}
                <Box sx={{ width: '100%', mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {user && user._id !== selectedMember._id && (
                    <Button
                      size="small"
                      variant="outlined"
                      fullWidth
                      onClick={() => {
                        handleMemberDetailsClose();
                        onStartDirectMessage(selectedMember);
                      }}
                    >
                      {t('comms.sendDirectMessage')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    fullWidth
                    startIcon={<GroupIcon />}
                    onClick={handleShowAllMembers}
                  >
                    {t('comms.viewMembers')}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          backgroundColor: 'background.default',
          minHeight: 0, // Crucial for flex child to properly scroll
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : messages.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {channel.isLocked 
              ? t('comms.noMessagesLockedChannel')
              : t('comms.noMessages')
            }
          </Alert>
        ) : (
          // Group consecutive messages by the same sender
          groupMessagesBySender(messages).map((group, index) => (
            <Box key={group[0]._id}>
              {/* First message in group shows avatar and username */}
              <MessageItem 
                key={group[0]._id}
                message={group[0]}
                currentUser={user}
                onDelete={handleDeleteMessage}
                onEdit={handleEditMessage}
                onStartDirectMessage={handleStartDirectMessage}
                showSender={true}
              />
              
              {/* Subsequent messages in group don't show avatar or username */}
              {group.slice(1).map((message) => (
                <MessageItem 
                  key={message._id}
                  message={message}
                  currentUser={user}
                  onDelete={handleDeleteMessage}
                  onEdit={handleEditMessage}
                  onStartDirectMessage={handleStartDirectMessage}
                  showSender={false}
                  isFollowUp={true}
                />
              ))}
            </Box>
          ))
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Message Input Area */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          boxShadow: '0 -1px 2px rgba(0,0,0,0.05)',
        }}
      >
        {channel.isLocked ? (
          <Alert 
            severity="warning" 
            icon={<LockIcon />} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              '& .MuiAlert-message': {
                flex: 1,
                display: 'flex',
                justifyContent: 'center'
              }
            }}
          >
            {t('comms.channelLockedMessage') || 'This channel is locked. No new messages can be sent.'}
          </Alert>
        ) : (
          <ClickAwayListener onClickAway={handleCloseEmojiPicker}>
            <Box sx={{ position: 'relative' }}>
              <Paper
                component="form"
                onSubmit={handleSendMessage}
                sx={{ 
                  p: '2px 4px', 
                  display: 'flex', 
                  alignItems: 'center',
                  position: 'relative',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  borderRadius: 2, // More rounded corners
                }}
                elevation={0}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder={t('comms.typeMesage')}
                  value={newMessage}
                  onChange={handleMessageChange}
                  onKeyDown={handleKeyDown}
                  inputRef={inputRef}
                  disabled={sendingMessage}
                  multiline
                  maxRows={4}
                  onClick={() => {
                    // Update cursor position when clicking in the input
                    if (inputRef.current) {
                      setCursorPosition(inputRef.current.selectionStart);
                    }
                  }}
                />
                <Tooltip title={t('comms.insertEmoji') || 'Insert emoji'}>
                  <IconButton 
                    ref={emojiButtonRef}
                    onClick={handleToggleEmojiPicker}
                    color={showEmojiPicker ? 'primary' : 'default'}
                  >
                    <EmojiIcon />
                  </IconButton>
                </Tooltip>
                <IconButton 
                  type="submit" 
                  color="primary" 
                  sx={{ p: '10px' }} 
                  disabled={!newMessage.trim() || sendingMessage}
                >
                  {sendingMessage ? (
                    <CircularProgress size={20} />
                  ) : (
                    <SendIcon />
                  )}
                </IconButton>
              </Paper>
              
              {/* Emoji Picker */}
              <Collapse in={showEmojiPicker} sx={{ 
                position: 'absolute', 
                bottom: '100%', 
                right: 0, 
                mb: 1,
                zIndex: 1400,
                boxShadow: 3,
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                <EmojiPicker 
                  onEmojiClick={handleEmojiClick} 
                  theme={isDarkMode ? "dark" : "light"}
                  width={300}
                  height={400}
                  skinTonesDisabled={false}
                  searchDisabled={false}
                  previewConfig={{
                    showPreview: true,
                    defaultCaption: t('comms.emojiPicker')
                  }}
                  lazyLoadEmojis={true}
                  emojiStyle="native"
                />
              </Collapse>
            </Box>
          </ClickAwayListener>
        )}
        
        {/* Mentions Popup */}
        <Popper
          open={Boolean(mentionAnchorEl) && mentionUsers.length > 0}
          anchorEl={mentionAnchorEl}
          placement="top-start"
          sx={{ zIndex: 1300, width: 300, maxHeight: 300, overflow: 'auto' }}
        >
          <Paper elevation={3} sx={{ mt: 1, mb: 1 }}>
            <List dense>
              {mentionUsers.length > 0 ? (
                mentionUsers.map((mentionUser) => (
                  <ListItem key={mentionUser._id} disablePadding>
                    <ListItemButton onClick={() => handleSelectMention(mentionUser)}>
                      <ListItemAvatar>
                        <Avatar
                          src={mentionUser.imageUrl}
                          alt={mentionUser.name || mentionUser.username}
                        >
                          {(mentionUser.name || mentionUser.username || '?')[0].toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={mentionUser.name || mentionUser.username}
                        secondary={mentionUser.username}
                      />
                    </ListItemButton>
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary={t('comms.noUsersFound')} />
                </ListItem>
              )}
            </List>
          </Paper>
        </Popper>
      </Box>
      
      {/* Edit Channel Dialog */}
      <EditChannelDialog
        open={editChannelDialogOpen}
        onClose={() => setEditChannelDialogOpen(false)}
        onEdit={handleEditChannel}
        channel={channel}
      />

      {/* Channel Members Dialog */}
      <Dialog 
        open={showMembersDialog} 
        onClose={() => setShowMembersDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {t('comms.channelMembers')} - {channel.name || getChannelDisplayName()}
            <Typography variant="body2" color="text.secondary">
              {new Set(channel.members?.map(m => m._id)).size} {new Set(channel.members?.map(m => m._id)).size === 1 ? t('comms.member') : t('comms.members')}
            </Typography>
          </Box>
          <Badge 
            badgeContent={new Set(channel.members?.map(m => m._id)).size}
            color="primary"
            max={999}
            sx={{ 
              '& .MuiBadge-badge': { 
                fontSize: '0.75rem', 
                height: 22, 
                minWidth: 22,
                padding: '0 6px'
              } 
            }}
          >
            <GroupIcon />
          </Badge>
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {/* Filter out duplicate members by ID */}
            {channel.members
              ?.filter((member, index, self) => 
                index === self.findIndex(m => 
                  m._id === member._id
                )
              )
              .map((member) => (
                <ListItem 
                  key={member._id}
                  secondaryAction={
                    user && (user.isAdmin || (channel.createdBy && channel.createdBy._id === user._id)) && 
                    member._id !== user._id && 
                    channel.type === 'team' && (
                      <IconButton 
                        edge="end" 
                        color="error"
                        size="small"
                        onClick={() => {
                          setShowMembersDialog(false);
                          const channelId = channel._id;
                          const userId = member._id;
                          if (onRemoveUser) onRemoveUser(channelId, userId);
                        }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <ListItemAvatar>
                    <Avatar 
                      src={member.imageUrl}
                      alt={member.name || member.username}
                    >
                      {(member.name || member.username || '?')[0].toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body1">
                          {member.name || member.username}
                        </Typography>
                        {channel.createdBy && channel.createdBy._id === member._id && (
                          <Typography 
                            variant="caption" 
                            sx={{ ml: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}
                          >
                            {t('comms.creator')}
                          </Typography>
                        )}
                        {member._id === user?._id && (
                          <Typography 
                            variant="caption" 
                            sx={{ ml: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'success.main', color: 'success.contrastText' }}
                          >
                            {t('comms.you')}
                          </Typography>
                        )}
                        {member.isAdmin && (
                          <Typography 
                            variant="caption" 
                            sx={{ ml: 1, px: 1, py: 0.5, borderRadius: 1, bgcolor: 'error.main', color: 'error.contrastText' }}
                          >
                            {t('admin.title')}
                          </Typography>
                        )}
                      </Box>
                    } 
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {member.username && member.name ? `@${member.username}` : ''}
                      </Typography>
                    }
                  />
                  {user && user._id !== member._id && (
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ml: 1 }}
                      onClick={() => {
                        setShowMembersDialog(false);
                        onStartDirectMessage(member);
                      }}
                    >
                      {t('comms.sendDirectMessage')}
                    </Button>
                  )}
                </ListItem>
              ))
            }
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMembersDialog(false)}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog for private channels */}
      <Dialog
        open={showDeleteConfirmDialog}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: 'error.main' }}>
          {t('comms.deleteConversationWarning')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {t('comms.deleteConversationDesc')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary" autoFocus>
            {t('cancel')}
          </Button>
          <Button onClick={handleDeleteChannel} color="error">
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChatWindow; 